import React, { useState } from 'react';
import ReactPixel from 'react-facebook-pixel';

const C = {
  navy: '#141C49',
  navyDeep: '#0d1235',
  navyMid: '#1a2460',
  sage: '#6C8F7A',
  sageTint: '#EFF4F1',
  warmGrey: '#F2EFE9',
  offWhite: '#F8F7F4',
  sand: '#C2A878',
};

const hl = (size: string): React.CSSProperties => ({
  fontFamily: '"Montserrat", "Open Sans", sans-serif',
  fontWeight: 800,
  fontSize: size,
});

type IconProps = { size?: number; primary?: string; accent?: string };
const BarChartIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="2" y="18" width="7" height="12" rx="1.5" fill={accent} opacity="0.65"/>
    <rect x="12.5" y="10" width="7" height="20" rx="1.5" fill={primary}/>
    <rect x="23" y="4" width="7" height="26" rx="1.5" fill={accent}/>
    <rect x="1" y="30" width="30" height="2" rx="1" fill={primary}/>
  </svg>
);
const CoinDropIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="12" r="9" fill={accent} opacity="0.22"/>
    <circle cx="16" cy="12" r="9" stroke={primary} strokeWidth="2.2"/>
    <path d="M16 7v1.5M16 15v1.5" stroke={primary} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M13.5 10c0-.83.67-1.5 1.5-1.5h1.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-1.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5H18c.83 0 1.5-.67 1.5-1.5" stroke={primary} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M16 21v3" stroke={accent} strokeWidth="2.5" strokeLinecap="round"/>
    <path d="M13 24l3 4 3-4" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const AlertCircleIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="13" fill={accent} opacity="0.18"/>
    <circle cx="16" cy="16" r="13" stroke={primary} strokeWidth="2"/>
    <path d="M16 9v9" stroke={primary} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="16" cy="23" r="1.8" fill={accent}/>
  </svg>
);
const HousesIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M19 7L9 15h3v11h14V15h3L19 7z" fill={accent} opacity="0.3"/>
    <path d="M19 7L9 15h3v11h14V15h3L19 7z" stroke={accent} strokeWidth="1.8" strokeLinejoin="round"/>
    <path d="M12 11L2 19h3v9h14v-9h3L12 11z" fill={primary} opacity="0.88"/>
    <rect x="10" y="23" width="4" height="5" rx="0.5" fill={accent}/>
  </svg>
);
const PillarsIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M16 2L31 8H1L16 2z" fill={accent}/>
    <rect x="2" y="8" width="28" height="3" rx="0.5" fill={primary}/>
    <rect x="5" y="11" width="4" height="17" rx="0.5" fill={accent} opacity="0.6"/>
    <rect x="14" y="11" width="4" height="17" rx="0.5" fill={primary}/>
    <rect x="23" y="11" width="4" height="17" rx="0.5" fill={accent} opacity="0.6"/>
    <rect x="2" y="28" width="28" height="3" rx="1" fill={primary}/>
  </svg>
);
const ClockIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="13" fill={accent} opacity="0.18"/>
    <circle cx="16" cy="16" r="13" stroke={primary} strokeWidth="2"/>
    <path d="M16 9v7l4.5 4.5" stroke={primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="16" cy="16" r="1.5" fill={accent}/>
  </svg>
);
const BookIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="5" y="3" width="22" height="26" rx="2" fill={accent} opacity="0.22"/>
    <rect x="5" y="3" width="22" height="26" rx="2" stroke={primary} strokeWidth="2"/>
    <rect x="5" y="3" width="5" height="26" rx="2" fill={primary} opacity="0.8"/>
    <path d="M14 11h9M14 16h9M14 21h6" stroke={accent} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const TrophyIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M10 4h12v11a6 6 0 01-12 0V4z" fill={accent} opacity="0.25"/>
    <path d="M10 4h12v11a6 6 0 01-12 0V4z" stroke={primary} strokeWidth="2"/>
    <path d="M10 8H5a3 3 0 003 3h2" stroke={primary} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M22 8h5a3 3 0 01-3 3h-2" stroke={primary} strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M16 22v4" stroke={primary} strokeWidth="2" strokeLinecap="round"/>
    <rect x="10" y="26" width="12" height="3" rx="1.5" fill={primary}/>
    <path d="M13 10l2 2 4-4" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const WalletIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="2" y="9" width="28" height="19" rx="3" fill={accent} opacity="0.2"/>
    <rect x="2" y="9" width="28" height="19" rx="3" stroke={primary} strokeWidth="2"/>
    <path d="M2 15h28" stroke={primary} strokeWidth="1.5"/>
    <rect x="20" y="18" width="8" height="5" rx="2" fill={primary}/>
    <circle cx="24" cy="20.5" r="1" fill={accent}/>
    <path d="M8 9V7a3 3 0 016 0v2" stroke={accent} strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const PhoneIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="8" y="2" width="16" height="28" rx="4" fill={accent} opacity="0.2"/>
    <rect x="8" y="2" width="16" height="28" rx="4" stroke={primary} strokeWidth="2"/>
    <path d="M13 6h6" stroke={primary} strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="16" cy="26" r="1.5" fill={accent}/>
    <path d="M12 15c0 4 8 4 8 0" stroke={accent} strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const TrendUpIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M3 26L11 17l7 5 11-14" stroke={primary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M22 8h8v8" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <rect x="1" y="29" width="30" height="2" rx="1" fill={primary} opacity="0.25"/>
  </svg>
);
const HomeIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M16 4L3 15h4v13h18V15h4L16 4z" fill={accent} opacity="0.2"/>
    <path d="M16 4L3 15h4v13h18V15h4L16 4z" stroke={primary} strokeWidth="2" strokeLinejoin="round"/>
    <rect x="12" y="19" width="8" height="9" rx="1" fill={primary}/>
    <rect x="8" y="16" width="5" height="5" rx="0.5" fill={accent}/>
    <rect x="19" y="16" width="5" height="5" rx="0.5" fill={accent}/>
  </svg>
);
const BriefcaseIcon: React.FC<IconProps> = ({ size = 32, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <rect x="3" y="11" width="26" height="18" rx="3" fill={accent} opacity="0.2"/>
    <rect x="3" y="11" width="26" height="18" rx="3" stroke={primary} strokeWidth="2"/>
    <path d="M11 11V8a2 2 0 012-2h6a2 2 0 012 2v3" stroke={primary} strokeWidth="2"/>
    <path d="M3 19h26" stroke={accent} strokeWidth="1.5"/>
    <rect x="14" y="17" width="4" height="4" rx="1" fill={primary}/>
  </svg>
);
const PinIcon: React.FC<IconProps> = ({ size = 14, primary = C.navy, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '2px', flexShrink: 0 }}>
    <path d="M7 1a4 4 0 00-4 4c0 3 4 8 4 8s4-5 4-8a4 4 0 00-4-4z" fill={accent} opacity="0.7"/>
    <circle cx="7" cy="5" r="1.5" fill={primary}/>
  </svg>
);
const TargetIcon: React.FC<IconProps> = ({ size = 20, primary = C.navyDeep, accent = C.sage }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' }}>
    <circle cx="10" cy="10" r="9" fill={accent} opacity="0.22"/>
    <circle cx="10" cy="10" r="9" stroke={primary} strokeWidth="1.6"/>
    <circle cx="10" cy="10" r="5.5" fill={accent} opacity="0.3"/>
    <circle cx="10" cy="10" r="5.5" stroke={primary} strokeWidth="1.3"/>
    <circle cx="10" cy="10" r="2.2" fill={primary}/>
  </svg>
);
const SparkleIcon: React.FC<IconProps> = ({ size = 16, primary = C.sage, accent = C.sand }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '5px' }}>
    <path d="M8 1l1.3 4.7L14 7l-4.7 1.3L8 13l-1.3-4.7L2 7l4.7-1.3L8 1z" fill={accent}/>
    <path d="M8 1l1.3 4.7L14 7l-4.7 1.3L8 13l-1.3-4.7L2 7l4.7-1.3L8 1z" stroke={primary} strokeWidth="0.8"/>
  </svg>
);

const faqItems = [
  {
    question: 'How does property investment through One Property Australia work?',
    answer: 'We provide end-to-end property education and strategic guidance — from identifying high-growth locations to understanding how investment property fits your goals. Our team analyses market data, government incentive eligibility, and your situation to connect you with the education, tools and licensed specialists you need.',
  },
  {
    question: 'Do I need a large deposit to get started?',
    answer: 'Not necessarily. Many Australians are surprised to find they already have sufficient equity or savings to enter the market. We also help you explore government grants, first home buyer incentives, and other schemes that can reduce the upfront cash required.',
  },
  {
    question: 'What is negative gearing and how can it benefit me?',
    answer: 'Negative gearing allows you to offset investment property losses against your taxable income — reducing the tax you pay. When combined with capital growth, it is a well-known approach used by many Australian property investors. We recommend speaking with a registered tax adviser to understand how it applies to your personal situation.',
  },
  {
    question: 'How do you identify the best property hotspots?',
    answer: 'We partner with advanced property analytics platforms that track infrastructure spending, population trends, rental vacancy rates, and supply-demand dynamics across every postcode in Australia. This data-driven approach removes guesswork from location selection.',
  },
  {
    question: 'Is the initial consultation free?',
    answer: 'Yes — your strategy session and eligibility assessment are completely free and obligation-free. We believe in demonstrating value before asking for any commitment.',
  },
];

const problems = [
  { icon: <BarChartIcon />, title: 'No Wealth Strategy', description: 'Most Australians lack a structured plan to build wealth beyond their family home, leaving retirement to chance.' },
  { icon: <CoinDropIcon />, title: 'Rising Cost of Living', description: 'Inflation is steadily eroding purchasing power. Wages alone cannot keep pace — passive income is no longer optional.' },
  { icon: <AlertCircleIcon />, title: "Super Won't Be Enough", description: "The median superannuation balance at retirement falls far short of what's needed for a comfortable lifestyle." },
  { icon: <HousesIcon />, title: 'Property Market Confusion', description: 'Without data, investors make emotional decisions — overpaying in the wrong suburb and missing real growth hotspots.' },
  { icon: <PillarsIcon />, title: 'Missed Tax Incentives', description: 'Billions in government grants and tax incentives go unclaimed every year by Australians who simply do not know they qualify.' },
  { icon: <ClockIcon />, title: 'Delayed Retirement Plans', description: 'Every year of inaction is compounding time lost. The investors who act early enjoy exponentially better outcomes.' },
];

const shiftSteps = [
  { emoji: '🌱', label: 'Health', desc: 'Financial wellbeing & clarity' },
  { emoji: '⚓', label: 'Stability', desc: 'Cash flow & debt managed' },
  { emoji: '🏗', label: 'Structure', desc: 'Entities, equity & planning' },
  { emoji: '📈', label: 'Growth', desc: 'Strategic property acquisition' },
  { emoji: '🌿', label: 'Legacy', desc: 'Generational wealth & freedom' },
];

const stats = [
  { number: '2,400', unit: '+', label: 'Australian families\nsupported' },
  { number: '$1.2', unit: 'B', label: 'Property assets\nunder guidance' },
  { number: '14', unit: 'yrs', label: 'Of strategic property\neducation' },
  { number: '4.9', unit: '★', label: 'Client experience\nrating' },
];

const testimonials = [
  {
    initials: 'SL',
    quote: 'One Property Australia changed how I think about building wealth. Within 12 months we had our first investment property producing positive cash flow. The team made the whole process feel simple.',
    name: 'Sarah L.',
    role: 'Registered Nurse, Brisbane QLD',
  },
  {
    initials: 'MK',
    quote: "I always thought property investing was only for people already wealthy. The team showed me how to leverage what I already had and redirected my tax into the mortgage. Genuinely life-changing.",
    name: 'Michael K.',
    role: 'Project Manager, Sydney NSW',
  },
  {
    initials: 'RP',
    quote: 'The Prosperity Engine framework gave us clarity we never had. We now have a 10-year roadmap and two properties already performing ahead of projections.',
    name: 'Rebecca P.',
    role: 'Small Business Owner, Gold Coast QLD',
  },
];

const ebookBenefits = [
  { title: 'Property Hotspot Analysis', body: 'Discover the suburbs primed for above-average capital growth based on infrastructure, demographics, and supply data.' },
  { title: 'Tax & Property Education', body: 'Understand how property investment can interact with your tax position — and the key questions to raise with your accountant or registered tax adviser.' },
  { title: 'Government Grant Opportunities', body: 'A comprehensive breakdown of federal and state incentives you may qualify for right now.' },
  { title: 'Retirement Wealth Planning', body: 'Explore how a strategic property portfolio may contribute to your retirement wealth, and the key factors that shape long-term outcomes.' },
  { title: 'Passive Income Blueprints', body: 'Step-by-step models showing how rental yield and equity work together to generate lasting income.' },
  { title: 'Debt Reduction Techniques', body: 'How to use investment property to pay down your own home faster — a strategy the wealthy use every day.' },
];

const insightCards = [
  { label: 'Capital Growth Zones', text: 'The suburbs poised for above-average growth in 2025 based on infrastructure investment and supply constraints.' },
  { label: 'Tax-Smart Structures', text: 'General education on how depreciation and negative gearing are commonly used by property investors. This is not tax advice — speak with a registered tax adviser for guidance specific to your situation.' },
  { label: 'Property & Superannuation', text: 'How property investment and superannuation can work together as part of a broader wealth plan. SMSF strategies require advice from a licensed financial adviser (AFSL holder).' },
];

const firstNames = ['Sarah', 'Michael', 'Emma', 'James', 'Jessica', 'Daniel', 'Sophie', 'Chris', 'Lauren', 'Matt', 'Rachel', 'Tom', 'Amy', 'Ben', 'Kate', 'David'];
const locations = [
  { city: 'Brisbane', state: 'QLD' }, { city: 'Sydney', state: 'NSW' }, { city: 'Melbourne', state: 'VIC' },
  { city: 'Gold Coast', state: 'QLD' }, { city: 'Perth', state: 'WA' }, { city: 'Adelaide', state: 'SA' },
  { city: 'Sunshine Coast', state: 'QLD' }, { city: 'Newcastle', state: 'NSW' }, { city: 'Toowoomba', state: 'QLD' },
];
const notifActions = [
  'just secured their property investment session', 'started their property report', 'booked their strategy call',
  'accessed their investment report', 'scheduled their property consultation', 'got their property analysis',
];
const timeAgoOptions = ['2 minutes ago', '5 minutes ago', '8 minutes ago', '12 minutes ago', '15 minutes ago'];

const q1Options = [
  { icon: <TrendUpIcon size={26} />, label: 'Build Long-Term Wealth' },
  { icon: <HomeIcon size={26} />, label: 'Buy My First Home' },
  { icon: <BriefcaseIcon size={26} />, label: 'Grow My Portfolio' },
  { icon: <WalletIcon size={26} />, label: 'Reduce My Tax Bill' },
];

const q2Options = ['Under $80,000', '$80,000 – $120,000', '$120,000 – $180,000', '$180,000+'];

const AssessmentFlow: React.FC<{ formId: string }> = ({ formId }) => {
  const [step, setStep] = useState<'q1' | 'q2' | 'processing' | 'form'>('q1');

  React.useEffect(() => {
    if (step === 'processing') {
      const t = setTimeout(() => setStep('form'), 2800);
      return () => clearTimeout(t);
    }
  }, [step]);

  React.useEffect(() => {
    if (step === 'form') {
      const existing = document.getElementById('ghl-embed-script');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.src = 'https://link.msgsndr.com/js/form_embed.js';
      script.async = true;
      script.id = 'ghl-embed-script';
      document.body.appendChild(script);
    }
  }, [step]);

  const progressMap = { q1: 20, q2: 55, processing: 80, form: 100 } as const;
  const labelMap = { q1: 'Step 1 of 3 — Your Goal', q2: 'Step 2 of 3 — Your Profile', processing: 'Preparing Your Playbook...', form: 'Step 3 of 3 — Claim Your Copy' } as const;

  return (
    <div>
      <div style={{ padding: '14px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '7px' }}>
          <span style={{ fontSize: '12px', color: C.sage, fontWeight: 700 }}>{labelMap[step]}</span>
          <span style={{ fontSize: '12px', color: '#aaa' }}>{progressMap[step]}% complete</span>
        </div>
        <div style={{ height: '5px', backgroundColor: '#e8edf0', borderRadius: '3px' }}>
          <div style={{ height: '100%', width: `${progressMap[step]}%`, background: `linear-gradient(90deg, ${C.sage}, #3d6b50)`, borderRadius: '3px', transition: 'width 0.5s ease' }} />
        </div>
      </div>

      {step === 'q1' && (
        <div style={{ padding: '20px 24px 24px' }}>
          <h2 style={{ ...hl('19px'), color: C.navy, margin: '0 0 4px', lineHeight: 1.25 }}>
            What's Your Wealth Goal?
          </h2>
          <p style={{ color: '#888', fontSize: '13px', margin: '0 0 18px', lineHeight: 1.5 }}>
            Tell us your goal — we'll personalise your free Property Wealth Playbook
          </p>
          <p style={{ color: C.navy, fontWeight: 700, fontSize: '14px', margin: '0 0 14px' }}>
            What's your primary property goal?
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {q1Options.map(opt => (
              <button
                key={opt.label}
                onClick={() => setStep('q2')}
                style={{
                  padding: '16px 10px', border: '2px solid #e2e8f0', borderRadius: '10px',
                  background: '#fff', cursor: 'pointer', textAlign: 'center',
                  transition: 'all 0.15s', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', gap: '8px', fontFamily: '"Open Sans", sans-serif',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.sage; e.currentTarget.style.backgroundColor = C.sageTint; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{opt.icon}</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: C.navy, lineHeight: 1.3 }}>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'q2' && (
        <div style={{ padding: '20px 24px 24px' }}>
          <h2 style={{ ...hl('19px'), color: C.navy, margin: '0 0 4px', lineHeight: 1.25 }}>
            One Step From Your Free Playbook
          </h2>
          <p style={{ color: '#888', fontSize: '13px', margin: '0 0 18px', lineHeight: 1.5 }}>
            This helps us highlight which strategies matter most for you.
          </p>
          <p style={{ color: C.navy, fontWeight: 700, fontSize: '14px', margin: '0 0 14px' }}>
            What's your combined household income?
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {q2Options.map(opt => (
              <button
                key={opt}
                onClick={() => setStep('processing')}
                style={{
                  padding: '13px 18px', border: '2px solid #e2e8f0', borderRadius: '10px',
                  background: '#fff', cursor: 'pointer', textAlign: 'left',
                  fontSize: '14px', fontWeight: 600, color: C.navy,
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', fontFamily: '"Open Sans", sans-serif',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.sage; e.currentTarget.style.backgroundColor = C.sageTint; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fff'; }}
              >
                <span>{opt}</span>
                <span style={{ color: C.sage, fontWeight: 700 }}>→</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'processing' && (
        <div style={{ padding: '44px 24px 48px', textAlign: 'center' }}>
          <div style={{
            width: '62px', height: '62px', border: `5px solid ${C.sageTint}`,
            borderTop: `5px solid ${C.sage}`, borderRadius: '50%',
            animation: 'spin 0.85s linear infinite', margin: '0 auto 22px',
          }} />
          <h3 style={{ ...hl('18px'), color: C.navy, margin: '0 0 10px', lineHeight: 1.3 }}>
            Building Your Property Wealth Playbook...
          </h3>
          <p style={{ color: '#999', fontSize: '14px', margin: 0, lineHeight: 1.7 }}>
            Preparing the property insights, growth hotspots &<br />income education most relevant to your situation...
          </p>
        </div>
      )}

      {step === 'form' && (
        <>
          <div style={{ padding: '14px 24px 4px' }}>
            <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: 700, color: C.sage, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'flex', alignItems: 'center' }}>
              <SparkleIcon />Your Free Playbook Is Ready to Send
            </p>
            <h2 style={{ ...hl('20px'), color: C.navy, margin: '0 0 5px', lineHeight: 1.25 }}>
              Claim My Free Property Wealth Playbook
            </h2>
            <p style={{ color: '#777', fontSize: '13px', lineHeight: 1.5, margin: 0 }}>
              Enter your details and we'll send your free copy instantly — no cost, no obligation.
            </p>
          </div>
          <iframe
            src="https://api.leadconnectorhq.com/widget/form/Xs0Nioqpf1h7u6H8mbok"
            style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: '520px', display: 'block' }}
            id={formId}
            title="Contact Form"
          />
        </>
      )}
    </div>
  );
};

const EasyPropertyInvestorHome: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrolled, setScrolled] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const [notifications, setNotifications] = useState<Array<{ id: number; message: string; location: string; timeAgo: string; visible: boolean }>>([]);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  React.useEffect(() => {
    ReactPixel.track('ViewContent', { content_name: 'Easy Property Investor' });
  }, []);

  // Lead conversion tracking for the GHL / LeadConnectorHQ iframe form.
  // NOTE: the form is a CROSS-ORIGIN iframe, so this page's pixel cannot observe the submit
  // directly — we can only react to postMessages GHL emits. This is best-effort hardening;
  // the authoritative fix is to set the Meta pixel INSIDE the GHL form settings
  // (form Xs0Nioqpf1h7u6H8mbok), which fires Lead from within the form's own origin.
  React.useEffect(() => {
    let leadFired = false; // fire-once guard — GHL emits the submit signal repeatedly
    const handleMessage = (event: MessageEvent) => {
      // Only trust messages from the GHL/LeadConnector iframe origin.
      if (!/(?:leadconnectorhq\.com|msgsndr\.com)$/.test(new URL(event.origin).hostname)) return;

      const data = event.data;
      // GHL signals a completed submission via a few different message shapes depending on
      // form/version. Treat any of these as the Lead conversion.
      const isSubmit =
        (Array.isArray(data) && (data[0] === 'set-sticky-contacts' || data[0] === 'form-submitted')) ||
        (typeof data === 'string' && /form[-_]?submit/i.test(data)) ||
        (data && typeof data === 'object' && (data.type === 'form-submitted' || data.event === 'form_submit'));

      if (isSubmit && !leadFired) {
        leadFired = true;
        ReactPixel.track('Lead', {
          content_name: 'Easy Property Investor',
          content_category: 'Property Investment',
          value: 0.0,
          currency: 'AUD',
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  React.useEffect(() => {
    const generateNotification = () => {
      const name = firstNames[Math.floor(Math.random() * firstNames.length)];
      const loc = locations[Math.floor(Math.random() * locations.length)];
      const action = notifActions[Math.floor(Math.random() * notifActions.length)];
      const time = timeAgoOptions[Math.floor(Math.random() * timeAgoOptions.length)];
      return { message: `${name} from ${loc.city} ${action}`, location: `${loc.city}, ${loc.state}`, timeAgo: time };
    };

    const show = (delay: number) => setTimeout(() => {
      const n = { id: Date.now(), ...generateNotification(), visible: true };
      setNotifications(prev => [...prev, n]);
      setTimeout(() => {
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, visible: false } : x));
        setTimeout(() => setNotifications(prev => prev.filter(x => x.id !== n.id)), 500);
      }, 8000);
    }, delay);

    const t1 = show(Math.floor(Math.random() * 17000) + 3000);
    const t2 = show(Math.floor(Math.random() * 30000) + 25000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const scrollToForm = () => {
    document.getElementById('final-cta')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ fontFamily: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#2c3e50', lineHeight: 1.6 }}>

      {/* ── NAVBAR ── */}
      <header style={{ backgroundColor: '#fff', position: 'sticky', top: 0, zIndex: 1000, boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.08)', transition: 'box-shadow 0.3s' }}>
        <nav style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '68px' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', lineHeight: 0 }}>
            <img src="/assets/onepropertyaustralia/logo.png" alt="One Property Australia" style={{ height: '56px', display: 'block' }} />
          </a>
          <button
            onClick={scrollToForm}
            style={{ backgroundColor: C.navy, color: '#fff', padding: '10px 22px', borderRadius: '50px', border: 'none', fontWeight: 600, fontSize: '14px', cursor: 'pointer', fontFamily: '"Open Sans", sans-serif', transition: 'background 0.2s', letterSpacing: '0.01em' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.navyDeep; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = C.navy; }}
          >
            Check My Eligibility
          </button>
        </nav>
      </header>

      {/* ── HERO ── */}
      <section style={{ backgroundColor: C.navy, minHeight: '100vh', padding: isMobile ? '50px 20px 60px' : '0 20px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ maxWidth: '1160px', margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 0.9fr', gap: isMobile ? '40px' : '70px', alignItems: 'flex-end', padding: isMobile ? '0' : '80px 0' }}>

          {/* Left: copy */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                ★ FREE PROPERTY EDUCATION RESOURCE ★ DOWNLOAD TODAY
              </span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }} />
            </div>

            <h1 style={{ ...hl(isMobile ? '36px' : '50px'), color: '#fff', lineHeight: 1.1, margin: '0 0 28px', maxWidth: '560px', textWrap: 'balance' } as React.CSSProperties}>
              How Australians Are Building{' '}
              <span style={{ color: C.sand }}>Generational Wealth</span>{' '}
              Through Strategic Property
            </h1>

            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '16px' : '18px', lineHeight: 1.7, margin: '0 0 28px', maxWidth: '500px', textWrap: 'pretty' } as React.CSSProperties}>
              Most high-income Australians earn well but structure poorly. Discover the property planning frameworks used by Australia's most financially deliberate families — free in The Property Wealth&nbsp;Playbook.
            </p>

            <iframe
              src="https://www.youtube.com/embed/wuJiS_FjVYI?autoplay=1&mute=1&loop=1&playlist=wuJiS_FjVYI&rel=0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', maxWidth: '500px', aspectRatio: '16/9', borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.45)', display: 'block', border: 'none' }}
              title="Property Wealth Video"
            />
          </div>

          {/* Right: assessment card */}
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: '#f0f4f8', padding: '14px 22px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #e2e8f0' }}>
              <img src="/docs/ebook.jpeg" alt="Free Ebook" style={{ width: '36px', height: '48px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0, boxShadow: '2px 2px 6px rgba(0,0,0,0.2)' }} />
              <div>
                <p style={{ margin: 0, fontSize: '10px', fontWeight: 700, color: C.sage, letterSpacing: '0.1em', textTransform: 'uppercase' }}>FREE PROPERTY WEALTH PLAYBOOK</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#444', fontWeight: 500, lineHeight: 1.3 }}>Personalise Your Copy — Takes 60 Seconds</p>
              </div>
            </div>
            <AssessmentFlow formId="msgsndr-Xs0Nioqpf1h7u6H8mbok" />
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section style={{ backgroundColor: C.offWhite, padding: isMobile ? '60px 20px' : '90px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '40px', marginBottom: '60px', alignItems: 'center' }}>
            <div>
              <p style={{ color: C.sage, fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>The Wealth Gap</p>
              <h2 style={{ ...hl(isMobile ? '28px' : '36px'), color: C.navy, lineHeight: 1.15, margin: 0, maxWidth: '420px', textWrap: 'balance' } as React.CSSProperties}>
                Why Most Australians Never Build Real Wealth
              </h2>
            </div>
            <p style={{ color: '#555', fontSize: '17px', lineHeight: 1.7, margin: 0 }}>
              The gap between those who retire wealthy and those who don't isn't talent or luck — it's strategy. The right property plan, executed at the right time, changes everything.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
            {problems.map(p => (
              <div
                key={p.title}
                style={{ backgroundColor: '#fff', padding: '28px', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', borderTop: `3px solid ${C.sage}`, transition: 'transform 0.2s, box-shadow 0.2s', cursor: 'default' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.07)'; }}
              >
                <div style={{ marginBottom: '14px', display: 'flex' }}>{p.icon}</div>
                <h3 style={{ ...hl('18px'), color: C.navy, margin: '0 0 10px' }}>{p.title}</h3>
                <p style={{ color: '#666', fontSize: '15px', lineHeight: 1.6, margin: 0 }}>{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EVERYTHING YOU GET FOR FREE ── */}
      <section style={{ backgroundColor: C.navy, padding: isMobile ? '60px 20px' : '90px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ textAlign: 'center', color: C.sand, fontWeight: 700, fontSize: '12px', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '14px' }}>
            NO COST · NO OBLIGATION · INSTANT ACCESS
          </p>
          <h2 style={{ ...hl(isMobile ? '28px' : '40px'), color: '#fff', textAlign: 'center', lineHeight: 1.15, margin: '0 auto 14px', maxWidth: '600px', textWrap: 'balance' } as React.CSSProperties}>
            Everything You Get For Free
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', textAlign: 'center', lineHeight: 1.6, margin: '0 auto 52px', maxWidth: '560px', textWrap: 'pretty' } as React.CSSProperties}>
            Complete your free assessment today and we'll send you The Property Wealth Playbook — plus everything below, at absolutely no&nbsp;cost.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '20px' }}>
            {[
              { icon: <BookIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'The Property Wealth Playbook', body: 'Our flagship guide covering property tax education, growth hotspots and passive income concepts — sent to your inbox the moment you complete your assessment.' },
              { icon: <TrophyIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'Property Hotspot Report', body: "2025's top-performing suburbs ranked by capital growth, rental yield, and infrastructure investment." },
              { icon: <PillarsIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'Grants & Incentives Breakdown', body: 'Every federal and state scheme you may qualify for right now, explained in plain language.' },
              { icon: <WalletIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'Property Tax Education Guide', body: 'A general overview of the tax concepts relevant to property investors — for discussion with your accountant or registered tax adviser.' },
              { icon: <PhoneIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'Free Strategy Consultation', body: 'A no-obligation call with one of our investment specialists — tailored to your situation.' },
              { icon: <BarChartIcon primary={C.sand} accent='rgba(255,255,255,0.75)' />, title: 'Personal Investment Blueprint', body: 'A starter roadmap based on your income, age, and goals so you know exactly where to begin.' },
            ].map(item => (
              <div key={item.title} style={{ backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: '12px', padding: '26px', border: '1px solid rgba(255,255,255,0.1)', transition: 'background 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; }}
              >
                <div style={{ marginBottom: '14px', display: 'flex' }}>{item.icon}</div>
                <p style={{ margin: '0 0 8px', color: '#fff', fontWeight: 700, fontSize: '16px', fontFamily: '"Montserrat", sans-serif', lineHeight: 1.3 }}>{item.title}</p>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '14px', lineHeight: 1.6 }}>{item.body}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '44px' }}>
            <button
              onClick={scrollToForm}
              style={{ backgroundColor: C.sand, color: C.navyDeep, padding: '16px 40px', borderRadius: '50px', border: 'none', fontWeight: 700, fontSize: '16px', cursor: 'pointer', fontFamily: '"Open Sans", sans-serif', transition: 'opacity 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            >
              Get My Free Playbook →
            </button>
          </div>
        </div>
      </section>

      {/* ── EBOOK ── */}
      <section style={{ backgroundColor: '#fff', padding: isMobile ? '60px 20px' : '90px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ textAlign: 'center', color: C.sage, fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Free Resource</p>
          <h2 style={{ ...hl(isMobile ? '28px' : '38px'), textAlign: 'center', color: C.navy, lineHeight: 1.2, margin: '0 auto 50px', maxWidth: '700px', textWrap: 'balance' } as React.CSSProperties}>
            "The Property Wealth Playbook" — Tax Strategies, Growth Hotspots & Passive Income Blueprints for Australian Investors
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.2fr', gap: '60px', alignItems: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src="/docs/ebook.jpeg"
                alt="The Property Wealth Playbook — Free Ebook"
                style={{ width: '260px', maxWidth: '100%', borderRadius: '8px', boxShadow: '8px 12px 32px rgba(0,0,0,0.2), -4px 4px 16px rgba(0,0,0,0.1)' }}
              />
            </div>
            <div>
              <p style={{ color: '#555', fontSize: '16px', lineHeight: 1.7, marginBottom: '24px' }}>
                Your complete guide to building, protecting and scaling wealth through strategic property investment — covering tax, hotspots, passive income and more.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '30px' }}>
                {ebookBenefits.map(b => (
                  <div key={b.title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: C.sageTint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                      <span style={{ color: '#1F3D2B', fontSize: '12px', fontWeight: 700 }}>✓</span>
                    </div>
                    <div>
                      <p style={{ margin: '0 0 2px', color: C.navy, fontWeight: 700, fontSize: '15px' }}>{b.title}</p>
                      <p style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: 1.6 }}>{b.body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={scrollToForm}
                style={{ backgroundColor: C.navy, color: '#fff', padding: '16px 36px', borderRadius: '4px', border: 'none', fontWeight: 700, fontSize: '16px', cursor: 'pointer', fontFamily: '"Open Sans", sans-serif', transition: 'background 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = C.navyDeep; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = C.navy; }}
              >
                Get My Free Playbook →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST ── */}
      <section style={{ backgroundColor: C.warmGrey, padding: isMobile ? '60px 20px' : '90px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ textAlign: 'center', color: C.sage, fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Why Clients Choose Us</p>
          <h2 style={{ ...hl(isMobile ? '28px' : '38px'), textAlign: 'center', color: C.navy, lineHeight: 1.2, margin: '0 0 50px', textWrap: 'balance' } as React.CSSProperties}>
            Trusted by Thousands of Australian Families
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '20px', marginBottom: '60px' }}>
            {stats.map(s => (
              <div key={s.label} style={{ textAlign: 'center', backgroundColor: '#fff', borderRadius: '10px', padding: '24px 16px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
                <div style={{ ...hl(isMobile ? '34px' : '42px'), color: C.navy, lineHeight: 1 }}>
                  {s.number}<span style={{ fontSize: isMobile ? '20px' : '26px' }}>{s.unit}</span>
                </div>
                <p style={{ color: '#666', fontSize: '14px', margin: '10px 0 0', lineHeight: 1.4, whiteSpace: 'pre-line' }}>{s.label}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
            {testimonials.map(t => (
              <div key={t.name} style={{ backgroundColor: '#fff', borderRadius: '10px', padding: '28px', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '18px', right: '18px', display: 'flex', gap: '1px' }}>
                  {[0, 1, 2, 3, 4].map(i => <span key={i} style={{ color: '#f59e0b', fontSize: '13px' }}>★</span>)}
                </div>
                <div style={{ color: '#ddd', fontSize: '48px', fontFamily: 'Georgia, serif', lineHeight: 0.8, marginBottom: '14px' }}>"</div>
                <p style={{ color: '#555', fontSize: '15px', fontStyle: 'italic', lineHeight: 1.7, marginBottom: '20px' }}>{t.quote}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: `linear-gradient(135deg, ${C.sage}, ${C.navy})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px', fontWeight: 700, flexShrink: 0 }}>
                    {t.initials}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, color: C.navy, fontSize: '14px' }}>{t.name}</p>
                    <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: '#aaa', fontSize: '12px', textAlign: 'center', marginTop: '32px', fontStyle: 'italic' }}>
            *Testimonials reflect individual experiences. Results vary based on personal circumstances, market conditions and investment decisions. Property investment involves risk and past performance is not indicative of future outcomes.
          </p>
        </div>
      </section>

      {/* ── EDUCATION ── */}
      <section style={{ backgroundColor: '#fff', padding: isMobile ? '60px 20px' : '90px 20px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p style={{ textAlign: 'center', color: C.sage, fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Learn More</p>
          <h2 style={{ ...hl(isMobile ? '28px' : '38px'), textAlign: 'center', color: C.navy, lineHeight: 1.2, margin: '0 0 50px', textWrap: 'balance' } as React.CSSProperties}>
            Common Questions & Key Insights
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 1fr', gap: '50px' }}>
            <div>
              {faqItems.map((item, i) => (
                <div key={i} style={{ borderBottom: `1px solid ${C.warmGrey}` }}>
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '16px' }}
                  >
                    <span style={{ color: C.navy, fontWeight: 600, fontSize: '15px', lineHeight: 1.4 }}>{item.question}</span>
                    <span style={{ color: C.sage, fontSize: '22px', flexShrink: 0, transition: 'transform 0.2s', transform: openFaqIndex === i ? 'rotate(45deg)' : 'rotate(0deg)', display: 'inline-block' }}>+</span>
                  </button>
                  {openFaqIndex === i && (
                    <div style={{ paddingBottom: '18px' }}>
                      <p style={{ color: '#666', fontSize: '15px', lineHeight: 1.7, margin: 0 }}>{item.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {insightCards.map(card => (
                <div key={card.label} style={{ backgroundColor: C.sageTint, borderRadius: '10px', padding: '24px', borderLeft: `4px solid ${C.sage}` }}>
                  <p style={{ ...hl('15px'), margin: '0 0 8px', color: C.navy }}>{card.label}</p>
                  <p style={{ margin: 0, color: '#555', fontSize: '14px', lineHeight: 1.6 }}>{card.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section id="final-cta" style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyDeep} 100%)`, padding: isMobile ? '60px 20px' : '90px 20px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(circle at 10% 50%, rgba(108,143,122,0.12) 0%, transparent 60%), radial-gradient(circle at 90% 30%, rgba(194,168,120,0.08) 0%, transparent 50%)`, pointerEvents: 'none' }} />
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '40px' : '60px', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div>
            <p style={{ color: C.sand, fontWeight: 700, fontSize: '13px', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '14px' }}>Take The First Step</p>
            <h2 style={{ ...hl(isMobile ? '30px' : '42px'), color: '#fff', lineHeight: 1.15, margin: '0 0 20px', textWrap: 'balance' } as React.CSSProperties}>
              Your Wealth Strategy Starts Here
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '17px', lineHeight: 1.7, marginBottom: '32px' }}>
              Complete your free 2-question assessment and claim your copy of The Property Wealth Playbook — packed with property insights, growth hotspots and passive income education built for Australians.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {['100% Free — No Hidden Costs', 'Obligation-Free Guarantee', 'Personalised to Your Situation', 'Brisbane-Based, Nationwide Coverage'].map(item => (
                <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: C.sand, fontSize: '16px', fontWeight: 700 }}>✓</span>
                  <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', fontWeight: 500 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ backgroundColor: C.sand, padding: '14px 24px', textAlign: 'center' }}>
              <p style={{ margin: 0, color: C.navyDeep, fontWeight: 700, fontSize: '15px', fontFamily: '"Montserrat", sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <TargetIcon /><span>Claim Your Free Property Wealth Playbook</span>
              </p>
            </div>
            <AssessmentFlow formId="msgsndr-Xs0Nioqpf1h7u6H8mbok-cta" />
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: '#fff', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '40px 20px 30px' : '50px 20px 30px' }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: '20px', marginBottom: '28px' }}>
            <img src="/assets/onepropertyaustralia/logo.png" alt="One Property Australia" style={{ height: '60px' }} />
            <div style={{ display: 'flex', gap: isMobile ? '14px' : '28px', flexWrap: 'wrap' }}>
              {[{ label: 'Privacy Policy', href: '/privacy-policy' }, { label: 'Terms and Conditions', href: '/terms-and-conditions' }, { label: 'Cookie Policy', href: '/cookie-policy' }].map(l => (
                <a key={l.href} href={l.href} style={{ color: '#666', fontSize: '14px', fontWeight: 600, textDecoration: 'none' }}>{l.label}</a>
              ))}
            </div>
          </div>
          <div style={{ color: '#666', fontSize: isMobile ? '10px' : '12px', lineHeight: 1.6, textAlign: 'justify' }}>
            <p><a href="https://onepropertyaustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onepropertyaustralia.com.au</a> is owned and operated by One Property Australia Pty Ltd (ABN 36 676 994 817) (referred to as "we", "us", or "One Property Australia"). We may contact you to provide the services you have requested. You are not obligated to accept or engage with any of these services. By submitting your information through <a href="https://onepropertyaustralia.com.au" style={{ color: '#a7a7a7', fontWeight: 'bold', textDecoration: 'none' }}>onepropertyaustralia.com.au</a>, you consent to being contacted by phone, SMS, or email regarding your enquiry. Please review our Privacy Policy for more details on how we manage and protect your information.</p>
            <p style={{ marginTop: '10px' }}>We do not provide financial product advice or financial dealing services, nor do we endorse financial products or issuers. Our primary service is property investment advisory and education. Where appropriate, we may connect you with third-party providers (such as building inspectors, property solicitors, property managers, or financial advisors) who may be able to assist with specific aspects of your property investment journey. Before engaging any provider, you should carefully assess whether their services are appropriate for your financial situation, needs, and goals.</p>
            <p style={{ marginTop: '10px' }}>We are not involved in delivering financial advice and take no responsibility for the services provided by third-party providers. To the fullest extent permitted by law, we are not liable for any loss or damage arising from your reliance on or use of services delivered by external providers.</p>
            <p style={{ marginTop: '10px' }}>One Property Australia Pty Ltd, part of One Group Australasia, may share your details with our sister companies One Finance Australia and One Solar Australia where we believe we can further support you in reducing living costs and increasing financial wealth. We may receive a fixed referral fee when passing your details to third-party providers. This fee is not commission-based and does not vary based on any investment decisions you may make.</p>
          </div>
          <p style={{ color: '#666', fontSize: '12px', marginTop: '20px', textAlign: 'center', fontStyle: 'italic' }}>
            * Historical growth is not an indication of future returns. No financial promise is made, only historical reporting.
          </p>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '10px', textAlign: 'center' }}>
            © 2025 One Property Australia. All rights reserved.
          </p>
        </div>
      </footer>

      {/* ── STICKY MOBILE CTA ── */}
      {isMobile && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999, padding: '12px 20px', backgroundColor: C.navy, boxShadow: '0 -4px 20px rgba(0,0,0,0.2)' }}>
          <button
            onClick={scrollToForm}
            style={{ width: '100%', backgroundColor: C.sand, color: C.navyDeep, padding: '14px', borderRadius: '4px', border: 'none', fontWeight: 700, fontSize: '16px', cursor: 'pointer', fontFamily: '"Montserrat", "Open Sans", sans-serif' }}
          >
            Get My Free Playbook — It's Free →
          </button>
        </div>
      )}

      {/* ── SOCIAL PROOF NOTIFICATIONS ── */}
      <div style={{ position: 'fixed', bottom: isMobile ? '80px' : '20px', left: '20px', zIndex: 9999, display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: isMobile ? 'calc(100% - 40px)' : '360px' }}>
        {notifications.filter(n => n.visible).map(n => (
          <div key={n.id} style={{ backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px', border: '1px solid rgba(0,0,0,0.08)', animation: 'slideInLeft 0.5s ease-out' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', backgroundColor: C.sageTint, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <HomeIcon size={22} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 3px', fontSize: '13px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1.4 }}>{n.message}</p>
              <p style={{ margin: 0, fontSize: '12px', color: '#888', display: 'flex', alignItems: 'center' }}><PinIcon />{n.location} · {n.timeAgo}</p>
            </div>
            <button
              onClick={() => setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, visible: false } : x))}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: '18px', padding: '0 2px', lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default EasyPropertyInvestorHome;
